type PrintOption = {
    id: number;
    storeId: number;
    name: string;
    title: string;
    displayName?: string;
    isBase: boolean;
    isUV?: boolean;
    desc4?: string;
    ins_color?: string;
    instIds?: number[];
    gradSchoolIds?: number[];
    fpc_id: number;
    show_all_gs?: boolean;
};

const normalize = (value: string = '') => (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const ids = (values: number[] = []) => [...new Set(values.filter(id => id > 0))].sort((a, b) => a - b);

// Cached products predating isUV still carry the method next to the colour in
// names such as UnmdLinoUVGoldMJC. Do not mistake institution codes like UVMT for UV.
const legacyUV = (option: PrintOption) => /\buv\b/i.test(option.title) || /uv[_ -]*(?:gold|silver|black|singlecolou?r|multicolou?r)|(?:gold|silver|black)[_ -]*uv/i.test(option.name);

export const isUVPrintOption = (option: PrintOption): boolean => option.isUV ?? legacyUV(option);

// A school's wordmark and its traditional lino can share the display name and colour
// (txamuvsinglecolorwordmark vs txamlinouvsinglecolor), so a wordmark only merges with a wordmark.
const isWordmark = (option: PrintOption) => /wordmark/i.test(option.name);

// Product-page lino dropdowns only: a wordmark's label ends in "Wordmark" (ahead of a
// " - Colour" suffix) so it reads differently from the traditional lino. The title itself is unchanged.
export const linoOptionLabel = (option: PrintOption, label: string): string => {
    if (!isWordmark(option) || /wordmark/i.test(label)) return label;
    const text = (label || '').trim();
    const suffix = option.desc4 ? ` - ${option.desc4}` : '';
    return suffix && text.endsWith(suffix) ? `${text.slice(0, -suffix.length)} Wordmark${suffix}` : `${text} Wordmark`;
};

const relativeColor = (option: PrintOption, kind: 'lino' | 'insignia') => {
    const color = normalize(kind === 'insignia' ? option.ins_color || option.desc4 : option.desc4);
    if (color) return color;
    // CAF linos in older caches have no desc4.
    return normalize(option.name).match(/(gold|silver|black)$/)?.[1] || '';
};

const artworkName = (option: PrintOption, kind: 'lino' | 'insignia') => {
    if (kind === 'lino' && option.displayName) {
        // The same lino text can have entirely different asset names for UV and embossing.
        return normalize(option.displayName)
            .replace(/\b(?:uv|embossed)\b/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    }
    // Insignia titles are generic subtype labels and old lino titles can be
    // generic school names. Use the asset name when the actual name is unavailable.
    return normalize(option.name)
        .replace(/uv(?=[_ -]*(?:gold|silver|black|singlecolou?r|multicolou?r))/g, '')
        .replace(/((?:gold|silver|black)[_ -]*)uv/g, '$1')
        .replace(/(?:[_ -]embossed(?=[_ -])|embossed(?=(?:gold|silver|black)))/g, '')
        .replace(/[_ -]/g, '');
};

export const getLinoOptionColor = (option: PrintOption): string => relativeColor(option, 'lino') || 'gold';

// Use the same artwork normalization when the category colour control switches
// between siblings, including a UV colour whose other colour is embossed-only.
export const getLinoOptionBase = (option: PrintOption): string =>
    artworkName(option, 'lino')
        .replace(/[_ -]?(black|gold|silver)$/, '')
        .replace(/lino$/, '')
        .replace(/[_ -]+$/, '')
        .trim();

export const getLinoOptionColorKey = (option: PrintOption): string => normalize(option.desc4);

// Label for the colour dropdown. A lino with no colour on file (blank desc4, which is
// every embossed "Dimensions" lino such as UmicLinoDimensions) would otherwise be a blank
// row next to "Black" and "Navy". The page only has the asset name and isUV, so the colour
// in the asset name, then the print method, stands in for the missing colour.
export const linoColorLabel = (option: PrintOption): string => {
    const color = (option.desc4 || '').trim();
    if (color) return linoOptionLabel(option, color);
    const named = relativeColor(option, 'lino');
    if (named) return linoOptionLabel(option, named.charAt(0).toUpperCase() + named.slice(1));
    const method = /dimensions/i.test(option.name) ? 'Dimensions' : isUVPrintOption(option) ? 'UV Printed' : 'Embossed';
    return linoOptionLabel(option, method);
};

// Group linos that share the same text but differ in colour, so the UI can offer
// one text dropdown plus a colour dropdown. Repeated colours get separate text
// entries so distinct artwork stays selectable without disabling other groups'
// colour choices. A wordmark stays in its text as one more colour choice,
// listed after the traditional lino of that colour.
export const groupLinoOptionsByText = <T extends PrintOption & {_title?: string}>(options: T[]) => {
    const groups: {key: string; label: string; items: T[]}[] = [];
    for (const option of options) {
        const color = (option.desc4 || '').trim();
        let label = (option._title || option.title || '').trim();
        if (color && label.toLowerCase().endsWith(color.toLowerCase())) {
            label = label
                .slice(0, label.length - color.length)
                .replace(/[\s-]+$/, '')
                .trim();
        }
        const key = JSON.stringify([normalize(option.displayName) || normalize(label), option.gradSchoolIds?.[0] || 0]);
        const group = groups.find(g => g.key === key);
        if (group) group.items.push(option);
        else groups.push({key, label: label || option.title, items: [option]});
    }
    for (const group of groups) group.items.sort((a, b) => Number(isWordmark(a)) - Number(isWordmark(b)));
    const colorChoice = (option: T) => `${getLinoOptionColorKey(option)}${isWordmark(option) ? ' wordmark' : ''}`;
    const unambiguousGroups = groups.flatMap(group => {
        const colorCounts = new Map<string, number>();
        for (const item of group.items) {
            const color = colorChoice(item);
            colorCounts.set(color, (colorCounts.get(color) || 0) + 1);
        }
        const uniqueColors = group.items.filter(item => colorCounts.get(colorChoice(item)) === 1);
        const repeatedColors = group.items.filter(item => colorCounts.get(colorChoice(item))! > 1);
        return [
            ...(uniqueColors.length ? [{...group, items: uniqueColors}] : []),
            ...repeatedColors.map((item, index) => ({
                key: JSON.stringify([group.key, 'option', item.id, index]),
                label: linoOptionLabel(item, item._title ?? item.title),
                items: [item],
            })),
        ];
    });
    return {groups: unambiguousGroups, hasColors: unambiguousGroups.some(group => group.items.length > 1)};
};

export const compressPrintOptions = <T extends PrintOption>(options: T[], kind: 'lino' | 'insignia', preferUV = true) => {
    const groups = new Map<string, T[]>();
    for (const [index, option] of options.entries()) {
        const color = relativeColor(option, kind);
        const name = artworkName(option, kind);
        // Unknown colours/names and synthetic choices must remain independently selectable.
        const key =
            option.id <= 0 || !color || !name ? JSON.stringify(['option', index]) : JSON.stringify([option.storeId, name, isWordmark(option), color, option.fpc_id, option.instIds?.[0] || 0, ids(option.instIds), option.gradSchoolIds?.[0] || 0, ids(option.gradSchoolIds), !!option.show_all_gs]);
        const group = groups.get(key);
        if (group) group.push(option);
        else groups.set(key, [option]);
    }

    const preferredIds = new Map<number, number>();
    const visible: T[] = [];
    for (const group of groups.values()) {
        const uv = group.find(isUVPrintOption);
        const preferred = preferUV ? uv : group.find(option => !isUVPrintOption(option));
        // An embossed product keeps the embossed version of matching artwork. Leave
        // unmatched artwork and embossed-only groups alone, as with the UV default.
        if (!uv || !preferred || group.length === 1) {
            visible.push(...group);
            continue;
        }
        const isBase = group.some(option => option.isBase);
        visible.push(isBase === preferred.isBase ? preferred : {...preferred, isBase});
        for (const option of group) preferredIds.set(option.id, preferred.id);
    }
    return {options: visible, preferredIds};
};

// A store only offers the insignias linked to its institution, except the product's own insignia:
// the product is built, priced and rendered with it, and its entity can be linked to no institution
// (instIds [-1]) or to a different one (Sentry CHC-WEB-FRONTEND-REACT-17R, product 269809).
export const offeredForInstitution = (option: PrintOption, institutionID: number, productOptionID: number): boolean =>
    (productOptionID > 0 && option.id === productOptionID) || !!option.instIds?.includes(institutionID);
